/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import { useMediaQuery } from '@material-ui/core';
import useFetchAPI from '../../common/customHooks/useFetchAPI';
import startConversation from '../../../../../apis/playbook-apis/startConversation'
import { uuidv4 } from '../../../../helpers/uuid';

import GetStarted from './GetStarted';
import PlayBookGeneration from './PlayBookGeneration';
import Loading from '../../common/Loading';
import PlayBookLoader from './PlayBookLoader';
import ConversationView from './ConversationComponents/ConversationView';
import GraphqlSeoHeadTags from '../../../../components/GraphqlComponents/GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { getFeatureFlags } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
// import RootedInLoveSVG from './RootedInLove';

const useStyles = makeStyles((theme) => ({
    rootContainer: {
        margin: '0 auto',
        lineHeight: 'normal',
        gap: '40px',
        '& .conversationInputRoot': {
            display: 'flex',
            justifyContent: 'center'
        },
        '& .inputAndArrowRoot': {
            width: '900px',
            height: '100%',
            paddingRight: 10,
            background: 'white',
            borderRadius: 8,
            border: '1px #979797 solid',
            justifyContent: 'flex-start',
            alignItems: 'center',
            display: 'inline-flex'
        },
        '& .arrowContainer': {
            padding: 4,
            background: '#007AFF',
            borderRadius: 8,
            cursor: 'pointer'
        },
        '& .disabledArrowContainer': {
            padding: 4,
            background: '#E0E0E0',
            borderRadius: 8,
            cursor: 'pointer'
        },
        '& input[type=text]': {
            minHeight: 'fit-Content',
            width: '900px',
            margin: 'auto',
            borderRadius: '6px',
            paddingLeft: '1.5rem',
            fontSize: '1.125rem !important',
            height: '3.125rem',
            paddingTop: 13,
            paddingBottom: 13,
            border: 0,
            [theme.breakpoints.down('sm')]: {
                width: '96%',
                zIndex: 999,
            },
        },
        '& h1': {
            color: '#B3261D !important',
            fontSize: '5rem !important',
            margin: '4px 0 0 0 !important',
            lineHeight: '4.5rem',
            [theme.breakpoints.down('sm')]: {
                fontSize: '3.5rem !important',
                lineHeight: '3.5rem !important',
                fontStyle: 'normal',
                textTransform: 'uppercase'
            },
        },
        '& h2': {
            fontFamily: 'Roboto Condensed, serif',
            fontSize: '1.125rem !important',
            color: '#B3261D !important',
            lineHeight: '16px',
            letterSpacing: '0.0625rem',
            fontWeight: '700 !important',
            margin: '0 !important',
            textAlign: 'center',
            fontStyle: 'normal',
        },
        '& .firstMsg': {
            [theme.breakpoints.down('sm')]: {
                marginTop: '13rem !important',
            },
        },
        //overiding spinner color to white
        '& .mbp-account-MuiSvgIcon-root rect': {
            fill: '#B3261D !important'
        },
        '& #rootedInSvgIcon': {
            position: 'absolute',
            zIndex: -1,
            transform: 'translate(28vw,28vh)',
            '& svg': {
                width: '26rem',
                height: '26rem',
            },
            [theme.breakpoints.down('sm')]: {
                zIndex: -1,
                position: 'absolute',
                top: '16rem',
                // left: '145px',
                right: '4rem',
                '& svg': {
                    width: '13rem',
                    height: '13rem',
                },
            },
        },
        '& .cardHeader': {
            fontSize: '3rem !important',
            fontFamily: 'Playfair Display, sans-serif!important',
            color: 'black',
            fontWeight: '500',
            lineHeight: '3rem',
            textAlign: 'center',
            fontStyle: 'normal',
            textTransform: 'uppercase',
            [theme.breakpoints.down('sm')]: {
                fontSize: '2.67rem !important',
            }
        },
        '& .cardSubHeader': {
            marginTop: '1rem',
            fontSize: '0.875rem !important',
            fontFamily: 'Roboto Mono, sans-serif!important',
            color: '#B3261D',
            fontWeight: 500,
            lineHeight: '1.75rem',
            textAlign: 'center',
            fontStyle: 'normal',
            textTransform: 'capitalize'
        },

        '& .productTitle': {
            fontSize: '1.125rem',
            fontWeight: 700,
            lineHeight: '1.75rem',
            marginBottom: 10,
            color: 'black',
            fontFamily: 'Raleway, sans-serif !important',
        },
        '& .productRange': {
            fontFamily: 'Lato, sans-serif !important',
            fontSize: 18,
            fontWeight: 500,
            lineHeight: '1.875rem',
            color: 'black',
            flexGrow: 1,
            height: 21,
            // marginBottom: '14px',
            [theme.breakpoints.down('sm')]: {
                fontWeight: 400,
            },
        },
        '& .cardImage': {
            width: '100%',
            height: '16.125rem',
            objectFit: 'contain',
            marginTop: '1rem',
        },
        '& .cardContent': {
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
        },
        '& .address a': {
            fontFamily: 'Lato, sans-serif !important',
            fontSize: 18,
            fontWeight: 500,
            lineHeight: '1.875rem',
            color: 'black',
            flexGrow: 1,
            height: 21,
            textDecoration: 'none',
        },
        '& .phoneNumber': {
            fontFamily: 'Lato, sans-serif !important',
            fontSize: 18,
            fontWeight: 500,
            lineHeight: '1.875rem',
            color: 'black',
            flexGrow: 1,
            height: 21,
        }
    },
    contentContainer: {
        height: '80%',
        margin: '3rem 1rem',
        fontFamily: 'Roboto Condensed, serif',
        display: 'flex',
        gap: '2rem',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        // [theme.breakpoints.down('sm')]: {
        //     textAlign: 'start',
        // },
        '& p': {
            // fontSize: '1.125rem',
            lineHeight: '30px',
            // fontFamily: 'Lato',
            fontSize: '18px !important',
            fontFamily: 'Roboto Condensed',
            maxWidth: '596px',
        },
        '& #aiDisclaimer': {
            fontSize: '14px !important',
            lineHeight: '22px',
            fontFamily: 'Lato !important',
            color: '#7D7D7D',
            [theme.breakpoints.down('sm')]: {
                fontSize: '10px !important',
            }
        }
    },
    contentCreatePlaybook: {
        // height: '100%',
        // height: 'calc(100vh - 175px)',
        // minHeight: 'calc(100vh - 366px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'start',
        margin: '0 15px 16px 15px',
        gap: '40px',
        overflowY: 'auto',
        [theme.breakpoints.down('sm')]: {
            // minHeight: 'calc(110vh - 276px)',
        },
        // maxHeight: 'calc(100vh - 100px)',
        '& .playbookBuilder': {
            fontSize: '16px',
            fontFamily: 'Lato, sans-serif!important',
            color: 'black'
        },
        '& .playbookMessage': {
            fontSize: '1.125rem !important',
            fontFamily: 'Lato, sans-serif!important',
            fontWeight: '400',
            lineHeight: '30px',
            letterSpacing: '0px',
        },
        '& .errorMsg': {
            color: 'red !important'
        },
        // '& .userMessage': {
        //     fontSize: '1.125rem !important',
        //     fontFamily: 'Lato, sans-serif!important',
        //     fontWeight: '400',
        //     lineHeight: '30px',
        //     letterSpacing: '0px',
        //     position: 'relative',
        //     backgroundColor: '#F5E2DC',
        //     display: 'flex',
        //     justifyContent: 'left',
        //     margin: '12px 0 12px auto',
        //     color: '#B3261D',
        //     borderRadius: '25px',
        //     padding: '6px 12px',
        //     width: 'fit-content',
        //     '&::before': {
        //         content: '""',
        //         width: '0px',
        //         height: '0px',
        //         position: 'absolute',
        //         borderRight: '9px solid #F5E2DC',
        //         borderLeft: '2px solid transparent',
        //         borderTop: '4px solid #F5E2DC',
        //         borderBottom: '18px solid transparent',
        //         right: '12px',
        //         bottom: '-12px',
        //     },
        // },
        '& .MuiButton-root': {
            fontFamily: 'Raleway, serif',
            backgroundColor: '#B3261D',
        },
    },
    messagesContainer: {
        // minHeight: 'fit-content',
        paddingTop: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        height: "calc(100vh - 390px)",
        overflow: 'auto',
        width: '900px',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            // height: "calc(100vh - 175px)",
            height: "calc(100vh - 283px)",
        },
    },
    button: {
        borderRadius: '6px !important',
        padding: '10px 30px',
        width: 'auto',
        textAlign: 'center',
        textTransform: 'uppercase',
        backgroundColor: '#B3261D',
        color: '#FFF',
        fontWeight: 700,
        // fontFamily: 'Raleway, serif !important'
    },
}))

const PlayBook = (props) => {
    const classes = useStyles();
    const [currentPlaybookStep, setCurrentPlaybookStep] = useState('get-started');
    const [userInput, setUserInput] = useState('');
    const [conversations, setConversations] = useState([]);
    const [userMessageIndex, setUserMessageIndex] = useState(-1);
    const [playbook, setPlaybook] = useState({});
    const [conversationId, setConversationId] = useState('');
    const featureFlags = useSelector(getFeatureFlags)
    const isPlayBookHiddenFromSearch = featureFlags['is-vday-playbook-page-hidden-from-search'];

    // Todo: Using mui for now, but if there is a existing state in redux/reducer we can also use that.
    const isMobile = useMediaQuery('(max-width: 600px)');

    const getSeoDataByBrand = () => {
        const seoData = {
            page_title: '',
            meta_description: '',
            canonicalUrl: 'https://www.1800flowers.com/account/vday-playbook',
            seo_searchable: !isPlayBookHiddenFromSearch,
        };
        switch (props.brandId) {
            case '18F':
                seoData.page_title = 'Valentine\'s Day Playbook | 1800Flowers';
                seoData.meta_description = "Plan the perfect Valentine's with our playbook! Share details about your date & our playbook will offer customized suggestions for an unforgettable experience.";
                break;
            case '18B':
                seoData.page_title = ' Valentine\'s Day Playbook |1800Baskets';
                seoData.meta_description = 'Plan an unforgettable Valentine\'s Date this year! Share details about your date & our playbook will offer customized suggestions for a perfect experience.';
                break;
            case 'SCH':
                seoData.page_title = 'Valentine\'s Day Playbook | Simply Chocolate';
                seoData.meta_description = 'Plan an unforgettable Valentine\'s Date this year! Share details about your date & our playbook will offer customized suggestions for a perfect experience.';
                break;
            case 'BRY':
                seoData.page_title = 'Valentine\'s Day Playbook | Shari\'s Berries';
                seoData.meta_description = 'Plan the perfect Valentine\'s Date this year! Share details about your date & our playbook will offer personalized suggestions for a perfect night!';
                break;
            case 'FB':
                seoData.page_title = ' Valentine\'s Day Playbook | Fruit Bouquets ';
                seoData.meta_description = 'Plan the ideal Valentine\'s experience! Share details about your date & our playbook will offer personalized suggestions for a perfect date!';
                break;
            case 'HD':
                seoData.page_title = ' Valentine\'s Day Playbook | Harry & David';
                seoData.meta_description = 'Plan the ideal Valentine\'s Date this year! Share details about your date & our playbook will offer personalized suggestions for an unforgettable experience.';
                break;
            case 'WLF':
                seoData.page_title = ' Valentine\'s Day Playbook | Wolferman\'s ';
                seoData.meta_description = 'Plan the perfect Valentine\'s with our playbook! Share details about your date & our playbook will offer customized suggestions for an unforgettable experience.';
                break;
            case 'VC':
                seoData.page_title = 'Valentine\'s Day Playbook | Vital Choice';
                seoData.meta_description = 'Plan the perfect Valentine\'s Day with us! Share details about your date & our playbook will offer customized suggestions for an unforgettable experience.';
                break;
            case 'CCO':
                seoData.page_title = ' Valentine\'s Day Playbook | Cheryl\'s';
                seoData.meta_description = 'Plan the perfect Valentine\'s Day this year! Share details about your special someone & our playbook will offer suggestions for an unforgettable experience.';
                break;
            case 'TPF':
                seoData.page_title = ' Valentine\'s Day Playbook | The Popcorn Factory';
                seoData.meta_description = 'Plan an unforgettable Valentine\'s Date this year! Share details about your date & our playbook will offer customized suggestions for a perfect experience.';
                break;
            default: break;
        }
        return seoData;
    };

    const chatMessagesEndRef = useRef(null);

    const userProfile = useSelector((state) => state.account.userProfile || state.member.profile);
    const scrollToBottom = () => {
        chatMessagesEndRef?.current?.scrollTo({
            top: chatMessagesEndRef?.current?.scrollHeight,
            behavior: 'smooth',
        });
    }
    const handleSubmit = () => {
        if (userInput && !createConversationMutation?.isLoading) {
            const updatedConversations = [...conversations];
            const index = userMessageIndex + 1;
            if (updatedConversations?.[index]) {
                updatedConversations[index].user = userInput;
            }
            setConversations(updatedConversations);
            setUserInput('');
            setUserMessageIndex(index);
        }
    }
    const handleUserInput = (e) => {
        setUserInput(e?.target?.value);
    }
    const handleKeyUp = (e) => {
        e?.preventDefault();
        if (e?.key === 'Enter') {
            handleSubmit();
        }
    }

    const onSuccess = (response) => {
        const result = response?.data?.result?.result;
        if (result) {
            const { message } = result;
            // Update conversation array/state
            setConversations((prevConversations) => [
                ...prevConversations,
                { ai: message, user: '' },
            ]);
            // Scroll to the bottom after updating the conversation
            if (userMessageIndex > -1) {
                scrollToBottom();
            }

            // if (conversationEnded) {
            if (result?.products?.length > 0) {
                const { events, meals, products, valentinesName, google, spotify } = result;

                // Handle conversation end logic
                setCurrentPlaybookStep('endconversation-playbook');
                window.scrollTo(0, 0)
                setTimeout(() => {
                    setPlaybook({
                        products,
                        google,
                        meals,
                        spotify,
                        events,
                        valentinesName
                    });
                }, 1000);
            }

        }
    };
    const onError = (err) => {
        console.log('error', err);
        setConversations((prevConversations) => [
            ...prevConversations,
            { ai: 'Playbook generator cannot be composed based on the input. Please try again with alternative inputs.', user: '', error: true },
        ]);
        console.log('error');
    };

    const createConversationMutation = useFetchAPI(onSuccess, onError, startConversation, true);
    const getHistory = () => {
        const history = [];
        conversations?.forEach(conversation => {
            if (conversation?.ai) {
                history.push(`AI: ${conversation.ai}`);
            }
            if (conversation?.user) {
                history.push(`Human: ${conversation.user}`);
            }
        });
        return history;
    }
    const hideGlobalFooter = () => {
        const footerElement = document.getElementById('global_footer_container');
        if (footerElement) {
            footerElement.style.display = 'none';
        }
    }
    useEffect(() => {
        hideGlobalFooter();
    }, []);

    useEffect(() => {
        if (userMessageIndex > -1) {
            const onGoingChatPayload =
            {
                history: `${getHistory()?.join('\n')}`,
                message: `${conversations?.[userMessageIndex]?.user}`,
                conversationId: conversationId,
            }
            if (userProfile.isAuthenticated) onGoingChatPayload.userId = userProfile.email;
            createConversationMutation.mutate(onGoingChatPayload);
            scrollToBottom();
        }
    }, [userMessageIndex])


    const handleGetStarted = async () => {
        // initPlaybookStep
        setCurrentPlaybookStep('start-playbook');
        const uuid = uuidv4();
        const initialChatPayload = {
            history: '',
            message: '',
            conversationId: uuid,
        }
        if (userProfile.isAuthenticated) initialChatPayload.userId = userProfile.email;
        setConversationId(uuid);
        // if (true) {
        //     setCurrentPlaybookStep('endconversation-playbook');
        //     const result = await import('./__mocks__/PlayBookMockResponse.json');
        //     const { events, meals, products, valentinesName, google, spotify } = result.result.result;

        //     // Handle conversation end logic
        //     setCurrentPlaybookStep('endconversation-playbook');
        //     window.scrollTo(0, 0)
        //     setTimeout(() => {
        //         setPlaybook({
        //             products,
        //             google,
        //             meals,
        //             spotify,
        //             events,
        //             valentinesName
        //         });
        //     }, 1000);
        // }
        createConversationMutation.mutate(initialChatPayload);
    }

    return (
        <>
            <GraphqlSeoHeadTags seoData={getSeoDataByBrand()} />
            <div className={classes.rootContainer}>
                {currentPlaybookStep === 'get-started' &&
                    <GetStarted classes={classes} onGetStarted={handleGetStarted} />
                }

                {currentPlaybookStep === 'start-playbook' && (
                    <>
                        {conversations.length > 0 ? (<div className={`${classes.contentCreatePlaybook}`}>
                            <div className={classes.messagesContainer} ref={chatMessagesEndRef}>
                                <ConversationView conversations={conversations} />
                            </div>
                        </div>) : <Loading />}
                        {conversations.length > 0 && (
                            <div className='conversationInputRoot'>
                                <div className='inputAndArrowRoot'>
                                    <input
                                        type="text"
                                        value={userInput}
                                        onChange={handleUserInput}
                                        onKeyUp={handleKeyUp}
                                        placeholder={createConversationMutation?.isLoading ? "AI is responding..." : "Enter message..."}>
                                    </input>
                                    <div className={(createConversationMutation?.isLoading || !userInput) ? 'disabledArrowContainer' : 'arrowContainer'} onClick={handleSubmit}>
                                        <div style={{ width: 24, height: 24, position: 'relative' }}>
                                            <ArrowUpwardIcon style={{ width: 24, height: 24, position: 'absolute', fill: 'white' }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {!isMobile}
                    </>
                )}

                {currentPlaybookStep === 'endconversation-playbook' && (
                    playbook?.products ? (
                        <PlayBookGeneration classes={classes} {...playbook} isMobile={isMobile} />
                    ) : (
                        <PlayBookLoader />
                    )
                )}

                {/* <div className={classes.underlay} /> */}
                {/* <div id='rootedInSvgIcon'>
                    <RootedInLoveSVG />
                </div> */}
            </div>
        </>
    );

};

export default PlayBook;


